import React, { useMemo, useState } from 'react';
import ConversationLogger, { warmSpeech } from '../components/home/ConversationLogger';
import DailyInterview from '../components/home/DailyInterview';
import { canConverse } from '../components/home/VoiceConversation';
import { Input } from '../components/ui/Input';
import { localToday } from '../utils/date';

// The capture screen. The daily check-in owns the page — full height, free to
// scroll — because it's the thing that gets used every day. Talking is a
// *session*: you enter it deliberately, so it needs a way in, not standing real
// estate. That way in is the capture bar docked at the bottom — always
// thumb-reachable no matter how tall the check-in grows — and tapping it opens
// the conversation as its own full-screen surface.
//
// This replaces the layout where the check-in card and the chat split one
// fixed-height window: every banner the card grew squeezed the chat (and its
// start button) toward zero, and the page's no-scroll rule made the remainder
// unreachable rather than merely far away.

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
       strokeWidth={2} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const MicIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
       strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round"
          d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z" />
  </svg>
);

const WalkieIcon = () => (
  // A handheld radio: body, antenna, speaker grille.
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
       strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <rect x="8" y="7" width="8" height="13" rx="1.5" strokeLinejoin="round" />
    <path strokeLinecap="round" d="M10 7V3.5M10.5 11h3M10.5 14h3M10.5 17h3" />
  </svg>
);

const LogPage = () => {
  const today = useMemo(() => localToday(), []);
  // The date lives here so the picker drives both the checklist and the chat.
  const [date, setDate] = useState(today);
  const [refreshKey, setRefreshKey] = useState(0);
  // Which conversation surface is open: null | 'type' | 'auto' | 'walkie'.
  const [chat, setChat] = useState(null);
  const bump = () => setRefreshKey((k) => k + 1);

  // Voice sessions must warm speech synthesis inside THIS tap (mobile autoplay
  // policy) — by the time the overlay mounts, the gesture is gone.
  const openVoice = (mode) => {
    warmSpeech();
    setChat(mode);
  };

  return (
    // One fixed-height column between the header and the bottom nav; the page
    // never scrolls the document, but the check-in pane scrolls freely inside
    // it, so the capture bar below it can never be pushed off screen. Layout
    // drops the footer + bottom padding on /log; we subtract the fixed header
    // (56px clearance) and bottom nav (60px) on mobile; desktop has neither.
    <div className="mx-auto flex w-full max-w-3xl flex-col px-4 sm:px-6 lg:px-8 h-[calc(100dvh-116px)] md:h-[calc(100dvh-2rem)]">
      {/* Just the date — the only thing here that changes what you're logging into. */}
      <div className="flex shrink-0 items-center justify-end pt-1.5 pb-2">
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          aria-label="Log date"
          className="!w-auto h-8 text-sm shrink-0"
        />
      </div>

      {/* The check-in owns everything down to the capture bar. */}
      <div className="min-h-0 flex-1 overflow-y-auto pb-3">
        <DailyInterview
          refreshKey={refreshKey}
          defaultDate={date}
          onLogged={bump}
        />
      </div>

      {/* The capture bar — the always-reachable way into a logging session. */}
      <div className="flex shrink-0 items-center gap-2 border-t border-surface-border dark:border-surface-dark-border pt-2 pb-3">
        <button
          type="button"
          onClick={() => setChat('type')}
          className="h-11 min-w-0 flex-1 truncate rounded-full border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card px-4 text-left text-sm text-gray-500 dark:text-gray-400 hover:border-primary-400 dark:hover:border-primary-500"
        >
          What did you eat, or do?
        </button>
        {canConverse && (
          <>
            <button
              type="button"
              onClick={() => openVoice('auto')}
              title="Auto voice — just talk; it replies when you pause"
              aria-label="Start auto voice logging"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card text-gray-700 dark:text-gray-200 hover:border-primary-400 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-400"
            >
              <MicIcon />
            </button>
            <button
              type="button"
              onClick={() => openVoice('walkie')}
              title="Walkie-talkie — talk as long as you like, then send"
              aria-label="Start walkie-talkie"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-500 text-[#1a1200] hover:bg-primary-400"
            >
              <WalkieIcon />
            </button>
          </>
        )}
      </div>

      {/* The conversation — a full-screen session surface, above the app chrome
          (header and bottom nav are z-50). The transcript lives only here now:
          it was ambient wallpaper competing for pixels; as a session it gets the
          whole screen. keyed by mode so re-opening starts the chosen surface
          fresh rather than resuming whatever mode the last session ended in. */}
      {chat && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-surface dark:bg-surface-dark">
          <div className="flex shrink-0 items-center justify-between border-b border-surface-border dark:border-surface-dark-border px-4 py-2.5">
            <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Log it · {date === today ? 'today' : date}
            </span>
            <button
              type="button"
              onClick={() => setChat(null)}
              aria-label="Close logging session"
              className="flex h-9 w-9 items-center justify-center rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-raised"
            >
              <CloseIcon />
            </button>
          </div>
          <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col p-3">
            <ConversationLogger
              key={chat}
              fill
              startIn={chat}
              defaultDate={date}
              onEntrySaved={bump}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LogPage;
